import type React from 'react'
import { cn } from '@/utils'
import { GEOMETRY_BLOCKED_PULSE_CLASS } from '@/components/app/editor/selection-chrome/geometry-blocked-feedback'
import {
  FROZEN_SELECTION_HIGHLIGHT_COLOR,
  SELECTION_HIGHLIGHT_COLOR,
  SELECTION_HIGHLIGHT_STROKE_WIDTH,
} from '../constants/selection-highlight'

export type ShapeOutlineVariant =
  'text-edit' | 'frozen' | 'alt-hover' | 'drop-target' | 'color-drop-target' | 'color-drop-applied'

interface OutlineStyle {
  fill: string
  stroke: string
  strokeWidth: number
  filter?: string
  /** Animation class from `index.css`. */
  className?: string
}

const OUTLINE_STYLES: Record<ShapeOutlineVariant, OutlineStyle> = {
  // Blue, same as a plain selection/create outline — not the red `--color-page-editor-flow-text`
  // used for overflow/link chrome elsewhere. Text-frame editing was deliberately blue (Figma
  // redesign), but that decision lived in the since-deleted separate text-frame renderer; this
  // shape-based path never carried it over.
  'text-edit': {
    fill: 'none',
    stroke: SELECTION_HIGHLIGHT_COLOR,
    strokeWidth: SELECTION_HIGHLIGHT_STROKE_WIDTH,
  },
  frozen: {
    fill: 'none',
    stroke: FROZEN_SELECTION_HIGHLIGHT_COLOR,
    strokeWidth: SELECTION_HIGHLIGHT_STROKE_WIDTH,
  },
  'alt-hover': {
    fill: 'none',
    stroke: SELECTION_HIGHLIGHT_COLOR,
    strokeWidth: 1,
    filter: 'drop-shadow(0 0 3px #0284C7)',
  },
  'drop-target': {
    fill: 'rgba(34,197,94,0.15)',
    stroke: '#16a34a',
    strokeWidth: 2,
    filter: 'drop-shadow(0 0 4px rgba(22,163,74,0.6))',
  },
  // A color swatch hovering a fillable shape: the whole shape is outlined and pops in, while the
  // body underneath previews the dragged color (no tint over it, so the preview reads true).
  'color-drop-target': {
    fill: 'none',
    stroke: SELECTION_HIGHLIGHT_COLOR,
    strokeWidth: 2,
    filter: 'drop-shadow(0 0 4px rgba(2,132,199,0.7))',
    className: 'color-drop-target-in',
  },
  // The color landed: one bright ring that swells and fades.
  'color-drop-applied': {
    fill: 'none',
    stroke: SELECTION_HIGHLIGHT_COLOR,
    strokeWidth: 0,
    className: 'color-drop-applied',
  },
}

interface ShapeOutlineProps {
  variant: ShapeOutlineVariant
  shapePath: string
  /** Frozen variant only: flash the geometry-blocked pulse. */
  pulse?: boolean
}

/** A non-interactive highlight traced along the shape's outline, holding its screen width at any zoom. */
export function ShapeOutline({ variant, shapePath, pulse = false }: ShapeOutlineProps) {
  const { fill, stroke, strokeWidth, filter, className } = OUTLINE_STYLES[variant]
  const style: React.CSSProperties | undefined = filter ? { filter } : undefined

  return (
    <path
      d={shapePath}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      shapeRendering="geometricPrecision"
      vectorEffect="non-scaling-stroke"
      strokeLinejoin="round"
      className={cn('pointer-events-none', className, pulse && GEOMETRY_BLOCKED_PULSE_CLASS)}
      style={style}
    />
  )
}
